/**
 * Implements software requirements: SRS-215
 */

import { computed, reactive, watch } from 'vue';
import { defineStore } from 'pinia';
import { taggedLogger } from '@/util/taggedLogger';
import { stop, stopAll, type StopHandle } from '@/util/stopHandle';
import { asyncTimeout } from '@/util/asyncTimeout';
import { asyncWatchUntil } from '@/util/asyncWatchUntil';
import { useHipSpineStore } from '@/views/3d-planner/hipSpine/hipSpineStore';
import { useHipSpinePostureSavePlan } from '@/views/3d-planner/hipSpine/useHipSpinePostureSavePlan';
import { useHipSpineXRayEditViewModel } from '@/views/3d-planner/hipSpine/xrayEdit/useHipSpineXRayEditViewModel';
import { StaleCaseError } from '@/api/caseVersion';

const log = taggedLogger('hip-spine-sync');

/** 3-state value that feeds `SaveStatusIndicator` directly. */
export type HipSpineSyncStatus = 'idle' | 'saving' | 'error';

/**
 * Debounce (ms) before a dirty posture is saved. Counted from the END of the
 * interaction — i.e. pointer-up after a landmark drag / femur-circle resize — so a save
 * never fires mid-drag (notably while the pointer is held still past the interval).
 */
const UPDATE_INTERVAL = 500;

/** Time in milliseconds between idle refreshes of non-dirty postures. */
const IDLE_REFRESH_INTERVAL = 5000;

/** Time in milliseconds to wait before retrying after a failed save. */
const RETRY_DELAY = 5000;

/** A queued debounced save: it fires once `Date.now() >= scheduledTime`. */
type QueuedUpdate = { scheduledTime: number };

/** The stores the sync engine drives. Resolved once when sync starts. */
type SyncDeps = {
    store: ReturnType<typeof useHipSpineStore>;
    savePlan: ReturnType<typeof useHipSpinePostureSavePlan>;
    editVm: ReturnType<typeof useHipSpineXRayEditViewModel>;
};

/** Mutable state of the synchroniser, shared with the module-level loop. */
type HipSpineSyncState = {
    status: HipSpineSyncStatus;
    queuedUpdate: QueuedUpdate | null;
};

export const useHipSpineSyncStore = defineStore('hip-spine-sync', () => {
    let syncing: StopHandle | null = null;

    const _state = reactive<HipSpineSyncState>({
        status: 'idle',
        queuedUpdate: null,
    });

    const status = computed(() => _state.status);

    return {
        status,
        isSaving: computed(() => _state.status === 'saving'),
        hasError: computed(() => _state.status === 'error'),
        startSync: () => {
            if (syncing) return;
            log.info('Start syncing hip-spine postures');
            _state.status = 'idle';
            _state.queuedUpdate = null;
            syncing = _sync(_state);
        },
        stopSync: () => {
            stop(syncing);
            syncing = null;
            _state.status = 'idle';
        },
    };
});

/** Snapshot of the dirty save-plans, used as the deep-watch change signal. */
function _dirtySnapshot(savePlan: SyncDeps['savePlan']): string {
    return JSON.stringify(savePlan.dirtyPostureIds.map((id) => savePlan.buildSavePlan(id)));
}

/**
 * True while the surgeon is mid-interaction (dragging a landmark or resizing a femur
 * circle). Saves are deferred until this clears so a drag is never interrupted — the
 * save fires on pointer-up + the debounce instead.
 */
function _isInteracting(deps: SyncDeps): boolean {
    return deps.editVm.dragState.kind !== 'idle';
}

function _sync(state: HipSpineSyncState): StopHandle {
    const aborter = new AbortController();
    const deps: SyncDeps = {
        store: useHipSpineStore(),
        savePlan: useHipSpinePostureSavePlan(),
        editVm: useHipSpineXRayEditViewModel(),
    };

    void _runGuarded(state, deps, aborter.signal);

    return stopAll(
        watch(
            () => _dirtySnapshot(deps.savePlan),
            () => {
                // Only (re)queue while something is actually dirty. A transition
                // TO clean — e.g. the post-save `clearPostureEdits`, or the user
                // reverting an edit — also changes the snapshot but must not
                // schedule a phantom no-op save.
                if (deps.savePlan.dirtyPostureIds.length === 0) return;
                state.queuedUpdate = { scheduledTime: Date.now() + UPDATE_INTERVAL };
            },
        ),
        watch(
            () => deps.editVm.dragState.kind,
            (kind) => {
                // Anchor the debounce to pointer-up: when a drag/resize ends with unsaved
                // edits, (re)start the debounce from release. The loop refuses to save
                // while `_isInteracting`, so this is what schedules the post-drag save —
                // a held-still drag no longer triggers a mid-drag save.
                if (kind === 'idle' && deps.savePlan.dirtyPostureIds.length > 0) {
                    state.queuedUpdate = { scheduledTime: Date.now() + UPDATE_INTERVAL };
                }
            },
        ),
        () => {
            if (!aborter.signal.aborted) {
                log.info('Stop syncing hip-spine postures');
                aborter.abort();
            }
        },
    );
}

/** Run the loop, swallowing abort-driven rejections so teardown stays quiet. */
async function _runGuarded(
    state: HipSpineSyncState,
    deps: SyncDeps,
    signal: AbortSignal,
): Promise<void> {
    try {
        await _run(state, deps, signal);
    } catch (error) {
        if (error instanceof StaleCaseError) {
            // A retry would be refused again; the stale-case overlay asks for a reload.
            log.warning('Hip-spine sync stopped: the case is stale');
            state.status = 'error';
        } else if (!signal.aborted) {
            log.error('Hip-spine sync loop stopped unexpectedly: %s', error);
        }
    }
}

async function _run(state: HipSpineSyncState, deps: SyncDeps, signal: AbortSignal): Promise<void> {
    while (!signal.aborted) {
        const queued = state.queuedUpdate;
        const interacting = _isInteracting(deps);
        if (queued && !interacting && Date.now() >= queued.scheduledTime) {
            await _saveDirtyPostures(state, deps, signal);
        } else if (queued) {
            // A save is queued but can't fire yet: it's either not due, or a drag/resize is
            // in progress (never save mid-interaction — the drag-end watch re-arms the
            // debounce from pointer-up). While interacting we can't predict pointer-up, so
            // poll at IDLE_REFRESH_INTERVAL; otherwise sleep exactly until due. Either wait
            // wakes early when the queue is re-armed (fresh edit / drag-end) or the drag
            // state changes.
            const dragKind = deps.editVm.dragState.kind;
            const delay = interacting
                ? IDLE_REFRESH_INTERVAL
                : Math.max(0, queued.scheduledTime - Date.now());
            await _waitForTimeoutOrChange(
                delay,
                () => state.queuedUpdate !== queued || deps.editVm.dragState.kind !== dragKind,
                signal,
            );
        } else {
            await _idleWait(state, deps, signal);
        }
    }
}

/** Wait the idle interval (waking if a save is queued); then refresh non-dirty postures. */
async function _idleWait(
    state: HipSpineSyncState,
    deps: SyncDeps,
    signal: AbortSignal,
): Promise<void> {
    await _waitForTimeoutOrChange(IDLE_REFRESH_INTERVAL, () => state.queuedUpdate !== null, signal);
    // Skip the refresh while the surgeon is mid-drag. `_isInteracting` flips true on
    // pointer-down — BEFORE the first coordinate change makes the posture dirty — so the
    // dirty-only guard alone would let a refresh through during that window and snap the
    // landmark back to server state mid-drag (it rebuilds geometry from the API response).
    if (
        !signal.aborted &&
        !state.queuedUpdate &&
        !_isInteracting(deps) &&
        deps.savePlan.dirtyPostureIds.length === 0
    ) {
        await _refreshNonDirtyPostures(deps, signal);
    }
}

/**
 * Persist every currently-dirty posture sequentially (no overlapping PUTs).
 * On success the posture's overlay is cleared; on failure the overlay is left
 * dirty, `status` flips to 'error', and a retry is re-queued. A stale case is
 * rethrown, not retried.
 */
async function _saveDirtyPostures(
    state: HipSpineSyncState,
    deps: SyncDeps,
    signal: AbortSignal,
): Promise<void> {
    state.status = 'saving';
    state.queuedUpdate = null;
    const dirtyIds = [...deps.savePlan.dirtyPostureIds];

    for (const id of dirtyIds) {
        if (signal.aborted) return;
        try {
            await deps.store.persistPosture(id, deps.savePlan.buildSavePlan(id), signal);
            deps.editVm.clearPostureEdits(id);
        } catch (error) {
            if (signal.aborted) return;
            if (error instanceof StaleCaseError) throw error;
            log.error('Failed to save hip-spine posture %s: %s', id, error);
            state.status = 'error';
            state.queuedUpdate = { scheduledTime: Date.now() + RETRY_DELAY };
            return;
        }
    }

    if (!signal.aborted) state.status = 'idle';
}

/**
 * Best-effort re-pull of every non-dirty posture. A refresh failure is
 * swallowed (logged only) — it must not flip `status` to 'error' or kill
 * the loop. Dirty postures are skipped entirely, and the WHOLE batch is
 * abandoned the moment a drag/resize starts mid-loop — `refreshPosture`
 * rebuilds geometry from server data, which would snap an in-progress edit.
 */
async function _refreshNonDirtyPostures(deps: SyncDeps, signal: AbortSignal): Promise<void> {
    const dirtyIds = new Set(deps.savePlan.dirtyPostureIds);
    const ids = deps.store.hipSpinePostures.map((posture) => posture.posture);

    for (const id of ids) {
        if (signal.aborted || _isInteracting(deps)) return;
        if (dirtyIds.has(id)) continue;
        try {
            await deps.store.refreshPosture(id, signal);
        } catch (error) {
            if (signal.aborted) return;
            log.warning('Idle refresh of hip-spine posture %s failed: %s', id, error);
        }
    }
}

/**
 * Resolve when `delay` elapses OR `change()` first becomes true — whichever
 * comes first. A local controller (also tripped by the outer `signal`) tears
 * down BOTH the timer and the reactive watch once the race settles, so no
 * `asyncWatchUntil` watcher is leaked across loop iterations.
 */
async function _waitForTimeoutOrChange(
    delay: number,
    change: () => boolean,
    signal: AbortSignal,
): Promise<void> {
    const local = new AbortController();
    const onOuterAbort = () => local.abort();
    signal.addEventListener('abort', onOuterAbort);
    // The losing branch is abandoned when the race settles; swallow its
    // (local-abort-driven) rejection so it never surfaces as unhandled.
    const changed = asyncWatchUntil(change, { signal: local.signal });
    const timeout = asyncTimeout(delay, local.signal).catch(() => undefined);
    try {
        await Promise.race([changed, timeout]);
    } finally {
        local.abort();
        signal.removeEventListener('abort', onOuterAbort);
    }
}
